/**
 * The stateful half of sync: talks to `/api/sync`, drives the stores, and
 * decides when to do either. The rules it applies are all in `merge.ts`; the
 * bookkeeping it reads and writes is all in `syncState.ts`.
 *
 * Offline-first throughout. localStorage stays the source of truth for every
 * screen, so nothing here is on the path to a render, and the whole module is
 * inert when signed out — which is also what a failed `/api/auth/me` looks
 * like, so `npm run dev` without a backend behaves exactly like a signed-out
 * device rather than breaking.
 */

import { allActivity, replaceActivity, type DayActivity, type RawActivityMap } from "@/hooks/useActivity";
import { replaceMastery } from "@/hooks/useMastery";
import { allSrsEntries, replaceSrs, type SrsMap } from "@/hooks/useSrs";
import type { SrsRecord } from "./srs";
import { deriveMastery } from "./study";
import {
  chunk,
  collectChanges,
  mergeDelta,
  mergeFirstSync,
  metaKey,
  nextCursor,
  reconcilePush,
  type FirstSyncPreview,
  type FirstSyncStrategy,
  type MetaMap,
  type SyncKind,
  type SyncRecord,
} from "./merge";
import {
  adoptAccount,
  getSyncState,
  markManyDirty,
  setCursor,
  setDirtyListener,
  setMeta,
  withRemoteApply,
} from "./syncState";

export interface SyncUser {
  id: string;
  email: string;
  name: string | null;
  picture: string | null;
}

/**
 * `needs-decision`: signed in to an account that already has progress while
 * this device has some too; nothing is written until the learner picks a
 * strategy in the first-sync dialog (`resolveFirstSync` / `cancelFirstSync`).
 */
export type SyncStatus = "signed-out" | "idle" | "syncing" | "error" | "needs-decision";

/** Must not exceed `MAX_PUSH` in `functions/lib/protocol.ts`. */
const PUSH_CHUNK = 40;
const PUSH_DEBOUNCE_MS = 2000;

let user: SyncUser | null = null;
let status: SyncStatus = "signed-out";
let lastError: string | null = null;
let pendingPreview: FirstSyncPreview | null = null;

const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of listeners) fn();
}

function setStatus(next: SyncStatus, error: string | null = null): void {
  status = next;
  lastError = error;
  emit();
}

export function subscribeSync(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function syncSnapshot(): {
  user: SyncUser | null;
  status: SyncStatus;
  error: string | null;
  /** Set while `status === "needs-decision"`: what the dialog shows. */
  pending: FirstSyncPreview | null;
} {
  return { user, status, error: lastError, pending: pendingPreview };
}

/* ---------------------------------------------------------------- transport */

async function api<T>(method: "GET" | "POST" | "DELETE", path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`${method} ${path} → ${response.status}`);
  return (await response.json()) as T;
}

/* ------------------------------------------------------------ store bridges */

function readStore(kind: SyncKind, key: string): unknown {
  return kind === "srs" ? allSrsEntries()[key] : allActivity()[key];
}

/**
 * Apply records that came from the server.
 *
 * `advanceCursor` is false for the rows echoed by a push: those are the
 * server's current state for the keys we just sent, not a delta. Taking a
 * cursor from them would skip every record with a lower `rev` that this device
 * has not pulled yet.
 */
export function applyRecords(records: readonly SyncRecord[], advanceCursor: boolean): void {
  if (records.length === 0) return;

  let meta: MetaMap = getSyncState().meta;

  const srs = mergeDelta<SrsRecord>(allSrsEntries(), meta, "srs", records);
  meta = srs.nextMeta;
  const activity = mergeDelta<DayActivity>(allActivity(), meta, "activity", records);
  meta = activity.nextMeta;

  if (srs.changed > 0 || activity.changed > 0) {
    withRemoteApply(() => {
      if (srs.changed > 0) {
        replaceSrs(srs.next);
        // The ledger is derived, never synced — re-project it from the
        // schedule we just merged so the two cannot disagree.
        replaceMastery(deriveMastery(srs.next));
      }
      if (activity.changed > 0) replaceActivity(activity.next);
    });
  }

  setMeta(meta);
  if (advanceCursor) setCursor(nextCursor(getSyncState().cursor, records));
}

/**
 * Replace everything with the account's state. Only ever used when adopting an
 * account this device hasn't synced before.
 *
 * The metadata is rebuilt from the pulled rows rather than merged into: any
 * flag left over from before — including a reset performed while signed out —
 * would otherwise fire immediately and delete the account's records.
 */
export function bootstrapFromServer(userId: string, records: readonly SyncRecord[]): void {
  const srs: SrsMap = {};
  const activity: RawActivityMap = {};
  const meta: MetaMap = {};

  for (const record of records) {
    meta[metaKey(record.kind, record.key)] = { at: record.updated_at, dirty: 0 };
    if (record.value === null) continue; // tombstone: remembered, not restored
    if (record.kind === "srs") srs[record.key] = JSON.parse(record.value) as SrsRecord;
    else activity[record.key] = JSON.parse(record.value) as DayActivity;
  }

  withRemoteApply(() => {
    replaceSrs(srs);
    replaceActivity(activity);
    replaceMastery(deriveMastery(srs));
  });

  adoptAccount(userId, nextCursor(0, records), meta);
}

/**
 * Keep the newer answer for every word, add study days together, remember the
 * account. Nothing either side knows is discarded except an older state of the
 * same word — the rule steady-state sync applies anyway. See `mergeFirstSync`.
 */
export function mergeWithServer(userId: string, records: readonly SyncRecord[], now = Date.now()): void {
  const merged = mergeFirstSync(allSrsEntries(), allActivity(), getSyncState().meta, records, now);
  withRemoteApply(() => {
    replaceSrs(merged.srs);
    replaceActivity(merged.activity);
    replaceMastery(deriveMastery(merged.srs));
  });
  adoptAccount(userId, nextCursor(0, records), merged.meta);
}

/** Flag everything on this device so a fresh account inherits it. */
function adoptLocalState(): void {
  const entries: { kind: SyncKind; key: string }[] = [
    ...Object.keys(allSrsEntries()).map((key) => ({ kind: "srs" as const, key })),
    ...Object.keys(allActivity()).map((key) => ({ kind: "activity" as const, key })),
  ];
  markManyDirty(entries);
}

/* ----------------------------------------------------------------- pull/push */

interface PullResponse {
  records: SyncRecord[];
  more: boolean;
}

/** Page through everything past `since`. */
async function pullFrom(since: number): Promise<SyncRecord[]> {
  const all: SyncRecord[] = [];
  let cursor = since;
  for (;;) {
    const page = await api<PullResponse>("GET", `/api/sync?since=${cursor}`);
    all.push(...page.records);
    if (!page.more || page.records.length === 0) break;
    const advanced = nextCursor(cursor, page.records);
    if (advanced <= cursor) break; // defensive: never loop on a stuck cursor
    cursor = advanced;
  }
  return all;
}

async function pull(): Promise<void> {
  applyRecords(await pullFrom(getSyncState().cursor), true);
}

async function push(): Promise<void> {
  const changes = collectChanges(getSyncState().meta, readStore);
  if (changes.length === 0) return;

  for (const batch of chunk(changes, PUSH_CHUNK)) {
    const sent: Record<string, number> = {};
    for (const change of batch) sent[metaKey(change.kind, change.key)] = change.updated_at;

    const echoed = (
      await api<{ records: SyncRecord[] }>("POST", "/api/sync", { changes: batch })
    ).records;

    // Clear flags before applying the echo, and only for keys untouched since
    // the request left — the learner may have answered more cards while it was
    // in flight.
    setMeta(reconcilePush(getSyncState().meta, sent));
    applyRecords(echoed, false);
  }
}

/**
 * Last-gasp push for a page that is being discarded.
 *
 * Fire-and-forget: nothing reads the echo and no flags are cleared, so the
 * next launch simply pushes the same records again — the conditional upsert
 * makes that a no-op. Not `sendBeacon`, which cannot carry the credentials or
 * report a rejection.
 */
function pushBeforeUnload(): void {
  const changes = collectChanges(getSyncState().meta, readStore);
  for (const batch of chunk(changes, PUSH_CHUNK)) {
    void fetch("/api/sync", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ changes: batch }),
      keepalive: true,
    }).catch(() => undefined);
  }
}

/* -------------------------------------------------------------- orchestration */

/** Serialises every network round trip; sync is never concurrent with itself. */
let queue: Promise<unknown> = Promise.resolve();

function run(job: () => Promise<void>): Promise<void> {
  const next = queue.then(async () => {
    if (user == null) return;
    setStatus("syncing");
    try {
      await job();
      setStatus("idle");
    } catch (error) {
      setStatus("error", error instanceof Error ? error.message : String(error));
    }
  });
  queue = next.catch(() => undefined);
  return next;
}

let pushTimer: ReturnType<typeof setTimeout> | null = null;

function schedulePush(): void {
  if (user == null) return;
  if (pushTimer != null) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    pushTimer = null;
    void run(() => push());
  }, PUSH_DEBOUNCE_MS);
}

function flushPush(): void {
  if (user == null) return;
  if (pushTimer != null) {
    clearTimeout(pushTimer);
    pushTimer = null;
  }
  pushBeforeUnload();
}

export type SignInCase = "known" | "empty-account" | "empty-device" | "conflict";

/** What `prepareSignIn` found, with the records already pulled so finishing needs no second pull. */
export interface SignInPlan {
  kind: SignInCase;
  records: SyncRecord[];
  preview: FirstSyncPreview | null;
}

/**
 * First half of signing in: find out what kind of account this is without
 * touching local state, so the UI can ask before anything is overwritten.
 */
export async function prepareSignIn(next: SyncUser): Promise<SignInPlan> {
  if (getSyncState().userId === next.id) return { kind: "known", records: [], preview: null };
  const records = await pullFrom(0);
  const remoteHasData = records.some((r) => r.value !== null);
  const localHasData =
    Object.keys(allSrsEntries()).length > 0 || Object.keys(allActivity()).length > 0;
  if (!remoteHasData) return { kind: "empty-account", records, preview: null };
  if (!localHasData) return { kind: "empty-device", records, preview: null };
  const preview = mergeFirstSync(allSrsEntries(), allActivity(), getSyncState().meta, records, Date.now()).preview;
  return { kind: "conflict", records, preview };
}

/**
 * Second half: attach the account and bring both sides together. `strategy`
 * only matters for a conflict.
 */
export async function finishSignIn(next: SyncUser, plan: SignInPlan, strategy: FirstSyncStrategy): Promise<void> {
  // Another tab may have attached the account while the dialog was open.
  const kind: SignInCase = getSyncState().userId === next.id ? "known" : plan.kind;
  switch (kind) {
    case "known":
      await pull();
      break;
    case "empty-account":
      // Nothing stored for this account yet: it inherits this device rather
      // than wiping it — silently discarding progress is the worst default.
      adoptAccount(next.id, 0, {});
      adoptLocalState();
      break;
    case "empty-device":
      bootstrapFromServer(next.id, plan.records);
      break;
    case "conflict":
      if (strategy === "use-account") bootstrapFromServer(next.id, plan.records);
      else mergeWithServer(next.id, plan.records);
      break;
  }
  await push();
}

/**
 * Sign in end to end. On a conflict the caller decides: `ask` returns the
 * strategy, or null to back out (nothing was written). Without `ask`, merge —
 * the one choice that never discards an answer.
 */
async function signInTo(
  next: SyncUser,
  ask?: (preview: FirstSyncPreview) => Promise<FirstSyncStrategy | null>
): Promise<void> {
  user = next;
  const plan = await prepareSignIn(next);
  let strategy: FirstSyncStrategy = "merge";
  if (plan.kind === "conflict" && ask) {
    const choice = await ask(plan.preview!);
    if (choice == null) {
      user = null;
      return;
    }
    strategy = choice;
  }
  await finishSignIn(next, plan, strategy);
}

/* --------------------------------------------------------- first-sync dialog */

let decide: ((choice: FirstSyncStrategy | null) => void) | null = null;

/** Parks the sign-in until the dialog answers. */
function askInDialog(preview: FirstSyncPreview): Promise<FirstSyncStrategy | null> {
  pendingPreview = preview;
  setStatus("needs-decision");
  return new Promise((resolve) => {
    decide = (choice) => {
      decide = null;
      pendingPreview = null;
      resolve(choice);
    };
  });
}

/** The learner picked a strategy in the first-sync dialog. */
export function resolveFirstSync(strategy: FirstSyncStrategy): void {
  decide?.(strategy);
}

/**
 * The learner backed out. Nothing was written; the cookie goes too, so the
 * next load does not quietly finish what was declined. Local edit timestamps
 * stay for the next attempt.
 */
export async function cancelFirstSync(): Promise<void> {
  decide?.(null);
  try {
    await api<{ user: null }>("POST", "/api/auth/logout");
  } finally {
    user = null;
    setStatus("signed-out");
  }
}

let triggersAttached = false;

function attachTriggers(): void {
  if (triggersAttached) return;
  triggersAttached = true;
  window.addEventListener("online", () => void run(() => push()));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void run(() => pull());
    else flushPush();
  });
  // Mobile Safari often never fires anything else before discarding the tab.
  window.addEventListener("pagehide", flushPush);
}

let started = false;

/**
 * Called once from `main.tsx` at module scope rather than from an effect —
 * `StrictMode` runs effects twice in development, and this must not be two
 * sign-in flows racing each other.
 */
export function startSync(): void {
  if (started) return;
  started = true;

  setDirtyListener(schedulePush);

  void (async () => {
    let session: { user: SyncUser | null };
    try {
      session = await api<{ user: SyncUser | null }>("GET", "/api/auth/me");
    } catch {
      // No backend (plain `npm run dev`) or offline at launch. Identical to
      // being signed out; a later trigger will try again.
      setStatus("signed-out");
      return;
    }

    if (session.user == null) {
      if (getSyncState().userId != null) adoptAccount(null);
      setStatus("signed-out");
      return;
    }

    setStatus("syncing");
    try {
      await signInTo(session.user, askInDialog);
      if (user == null) return; // backed out of the dialog
      setStatus("idle");
    } catch (error) {
      setStatus("error", error instanceof Error ? error.message : String(error));
    }

    attachTriggers();
  })();
}

/** Sign out and stop syncing. Local progress stays on the device. */
export async function signOut(): Promise<void> {
  try {
    await api<{ user: null }>("POST", "/api/auth/logout");
  } finally {
    user = null;
    // Only an attached account leaves bookkeeping that must not leak into the
    // next one. A device that never attached keeps its local edit timestamps —
    // the first-sign-in merge is built on them.
    if (getSyncState().userId != null) adoptAccount(null);
    setStatus("signed-out");
  }
}

export const SIGN_IN_URL = "/api/auth/google";

/**
 * Delete the account and every record synced under it, then sign this device
 * out. Local progress stays; the learner can export it or keep studying.
 */
export async function deleteAccount(): Promise<void> {
  await api<{ deleted: true }>("DELETE", "/api/account");
  user = null;
  if (getSyncState().userId != null) adoptAccount(null);
  setStatus("signed-out");
}
